import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Eye, EyeOff, Mail, Lock, AlertCircle, LogIn } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

type LoginFormProps = {
  setShowPassword: (show: boolean) => void;
  showPassword: boolean;
};

export default function LoginForm({ showPassword, setShowPassword }: LoginFormProps) {
  const { signIn, isGoogleAccount: checkIsGoogleAuthAccount } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleAccount, setIsGoogleAccount] = useState(false);

  const loginForm = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onEmailBlur = async () => {
    try {
      const email = loginForm.getValues("email");
      if (email && loginSchema.shape.email.safeParse(email).success) {
        const isGoogle = await checkIsGoogleAuthAccount(email);
        setIsGoogleAccount(isGoogle);
        
        if (isGoogle) {
          console.log('Google account detected on blur');
        }
      }
    } catch (error) {
      console.error('Error checking if email is Google account:', error);
    }
  };

  const onLogin = async (formData: z.infer<typeof loginSchema>) => {
    console.log('Login attempt with email:', formData.email);
    setIsLoading(true);
    
    try {
      console.log('Calling signIn method...');
      await signIn(formData.email, formData.password);
      console.log('SignIn completed successfully');
      
      toast({
        title: "Login successful",
        description: "Welcome back!",
      });
      console.log('Navigating to /home');
      navigate('/home');
    } catch (error: any) {
      console.error('Login error:', error);
      
      // Check if the error indicates this might be a Google account
      if (error.message?.toLowerCase().includes('invalid login credentials') || 
          error.message?.toLowerCase().includes('invalid email or password')) {
        
        // Use the helper method to check if this is a Google account
        const isGoogle = await checkIsGoogleAuthAccount(formData.email);
        if (isGoogle) {
          setIsGoogleAccount(true);
          toast({
            title: "Google account detected",
            description: "This email is registered with Google. You can use Google sign-in or enter your password if you've set one.",
            variant: "default",
          });
        } else {
          toast({
            title: "Login failed",
            description: error.message || "Please check your credentials and try again",
            variant: "destructive",
          });
        }
      } else {
        toast({
          title: "Login failed",
          description: error.message || "Please check your credentials and try again",
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
      console.log('Login attempt completed');
    }
  };

  return (
    <Form {...loginForm}>
      <form onSubmit={loginForm.handleSubmit(onLogin)} className="space-y-4">
        {isGoogleAccount && (
          <Alert variant="default" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              This email is registered with Google. You can use the Google sign-in button below.
              <br />
              <span className="text-xs mt-1 block text-muted-foreground">
                If you previously set a password for this account, you can also sign in with that password.
              </span>
            </AlertDescription>
          </Alert>
        )}
      
        <FormField
          control={loginForm.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <div className="relative">
                <FormControl>
                  <Input 
                    placeholder="you@example.com" 
                    {...field} 
                    onBlur={onEmailBlur}
                  />
                </FormControl>
                <Mail className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={loginForm.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Password</FormLabel>
              <div className="relative">
                <FormControl>
                  <Input 
                    type={showPassword ? "text" : "password"} 
                    placeholder="••••••••" 
                    {...field} 
                  />
                </FormControl>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </Button>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <Button 
          type="submit" 
          className="w-full relative overflow-hidden bg-vibebops-card border-4 border-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-1 hover:translate-y-1 transition-all duration-200 group"
          disabled={isLoading}
        >
          {/* Circuit board pattern */}
          <div className="absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity duration-300" 
            style={{ 
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v100H0z' fill='none'/%3E%3Cpath d='M10 10h10v10H10zM30 10h10v10H30zM50 10h10v10H50zM70 10h10v10H70zM90 10h10v10H90zM10 30h10v10H10zM30 30h10v10H30zM50 30h10v10H50zM70 30h10v10H70zM90 30h10v10H90zM10 50h10v10H10zM30 50h10v10H30zM50 50h10v10H50zM70 50h10v10H70zM90 50h10v10H90zM10 70h10v10H10zM30 70h10v10H30zM50 70h10v10H50zM70 70h10v10H70zM90 70h10v10H90zM10 90h10v10H10zM30 90h10v10H30zM50 90h10v10H50zM70 90h10v10H70zM90 90h10v10H90z' stroke='%2366BBFF' stroke-opacity='1' stroke-width='1' fill='none'/%3E%3C/svg%3E")`,
              backgroundSize: '60px 60px'
            }}>
          </div>
          
          {/* Glowing accent border */}
          <div className="absolute inset-0 p-[4px] overflow-hidden pointer-events-none">
            <div className="absolute inset-0 transform rotate-0 group-hover:rotate-180 transition-transform duration-1000 ease-in-out">
              <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-vibebops-accent2 to-transparent absolute top-0 left-0"></div>
              <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-vibebops-accent to-transparent absolute bottom-0 left-0"></div>
              <div className="w-[1px] h-full bg-gradient-to-b from-transparent via-vibebops-accent2 to-transparent absolute left-0 top-0"></div>
              <div className="w-[1px] h-full bg-gradient-to-b from-transparent via-vibebops-accent to-transparent absolute right-0 top-0"></div>
            </div>
          </div>
          
          {/* Moving data dots */}
          <div className="absolute inset-0">
            <div className="absolute top-0 left-[10%] w-1 h-1 bg-vibebops-accent2 rounded-full animate-data-packet opacity-0 group-hover:opacity-100" style={{ '--path-x': '80%', '--path-y': '90%', animationDelay: '0s' } as React.CSSProperties}></div>
            <div className="absolute bottom-0 right-[10%] w-1 h-1 bg-vibebops-accent rounded-full animate-data-packet opacity-0 group-hover:opacity-100" style={{ '--path-x': '-80%', '--path-y': '-90%', animationDelay: '0.4s' } as React.CSSProperties}></div>
            <div className="absolute top-[50%] left-0 w-1 h-1 bg-white rounded-full animate-data-packet opacity-0 group-hover:opacity-100" style={{ '--path-x': '100%', '--path-y': '0%', animationDelay: '0.8s' } as React.CSSProperties}></div>
          </div>
          
          {/* Glitch effect on hover */}
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
            <span className="absolute text-vibebops-accent2 font-bold transform translate-x-[1px] translate-y-[1px] animate-pulse-subtle">{isLoading ? "Logging in..." : "Login"}</span>
            <span className="absolute text-vibebops-accent font-bold transform -translate-x-[1px] -translate-y-[1px] animate-pulse-subtle" style={{ animationDelay: '0.2s' }}>{isLoading ? "Logging in..." : "Login"}</span>
          </div>
          
          {/* Content with icon */}
          <div className="relative z-10 flex items-center justify-center group-hover:opacity-0 transition-opacity duration-300">
            <LogIn className="mr-2 h-5 w-5 text-vibebops-accent2" />
            <span className="text-vibebops-accent font-bold">{isLoading ? "Logging in..." : "Login"}</span>
          </div>
        </Button>
      </form>
    </Form>
  );
}
